import { createContext, useContext } from 'react';

/**
 * Creates a React context and hook that ensures the value is never `null`.
 * Throws a custom error if used outside its provider.
 *
 * @template ContextValue - Type of the context value.
 * @param {string} errorMessage - Error message when context is missing.
 * @returns {[Provider: React.FC<{ value: ContextValue; children: React.ReactNode }>, useSafeContext: () => ContextValue]}
 *
 * @example
 * const [UserProvider, useUser] = createSafeContext<User>('Must be inside UserProvider');
 *
 * <UserProvider value={{ name: 'Alice' }}>
 *   <Profile />
 * </UserProvider>
 */
export function createSafeContext<ContextValue>(errorMessage: string) {
    const Context = createContext<ContextValue | null>(null);

    const useSafeContext = () => {
        const ctx = useContext(Context);

        if (ctx === null) {
            throw new Error(errorMessage);
        }

        return ctx;
    };

    function Provider({ children, value }: Readonly<{ value: ContextValue; children: React.ReactNode }>) {
        return <Context.Provider value={value}>{children}</Context.Provider>;
    }

    return [Provider, useSafeContext] as const;
}
